import React, { useCallback, useEffect, useState } from 'react';
import { getCredits, getCreditCosts, getCreditPacks, purchaseCredits, getCreditLedger } from '../api/endpoints';
import type {
  Credits,
  FeatureCost,
  CreditPack,
  CreditLedgerEntry,
} from '../types/api';
import { useUser } from '../contexts/UserContext';
import Loading from '../components/Loading';
import Coin from '../components/kivo/Coin';
import { getErrorMessage } from '../utils/errors';

const FEATURE_LABELS: Record<string, string> = {
  basic_skin_scan: 'Basic skin scan',
  product_barcode: 'Product barcode scan',
  product_image: 'Product image scan',
  premium_face: 'Premium skin read',
  hair_analysis: 'Hair analysis',
  makeup_analysis: 'Makeup analysis',
  product_compare: 'Product comparison',
  product_compat: 'Compatibility check',
};

const EVENT_LABELS: Record<string, string> = {
  consume: 'Used',
  refund: 'Refunded',
  grant: 'Monthly grant',
  plan_change: 'Plan change',
  plan_refund: 'Plan refund',
  purchase: 'Purchase',
  purchase_refund: 'Purchase refund',
  admin_adjust: 'Adjustment',
};

const LEDGER_PAGE_SIZE = 20;

function formatPrice(cents: number, currency: string): string {
  const amount = (cents / 100).toFixed(2);
  const symbol = currency === 'EUR' ? '€' : currency === 'USD' ? '$' : `${currency} `;
  return `${symbol}${amount}`;
}

export default function WalletPage(): React.JSX.Element {
  const { refreshCredits } = useUser();
  const [credits, setCredits] = useState<Credits | null>(null);
  const [costs, setCosts] = useState<FeatureCost[]>([]);
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchasingId, setPurchasingId] = useState<string | null>(null);
  const [purchaseMsg, setPurchaseMsg] = useState<string | null>(null);
  const [purchaseErr, setPurchaseErr] = useState<string | null>(null);

  const [ledger, setLedger] = useState<CreditLedgerEntry[]>([]);
  const [ledgerTotal, setLedgerTotal] = useState(0);
  const [ledgerOffset, setLedgerOffset] = useState(0);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  const loadLedger = useCallback(async (offset: number) => {
    setLedgerLoading(true);
    try {
      const res = await getCreditLedger(LEDGER_PAGE_SIZE, offset);
      setLedger(res.entries);
      setLedgerTotal(res.total);
      setLedgerOffset(offset);
    } catch {
      /* ignore */
    } finally {
      setLedgerLoading(false);
    }
  }, []);

  useEffect(() => {
    const cancelledRef = { current: false };
    void (async () => {
      const [c, fc, p] = await Promise.allSettled([
        getCredits(),
        getCreditCosts(),
        getCreditPacks(),
      ]);
      if (cancelledRef.current) return;
      if (c.status === 'fulfilled') setCredits(c.value);
      if (fc.status === 'fulfilled') setCosts(fc.value);
      if (p.status === 'fulfilled') setPacks(p.value);
      setLoading(false);
    })();
    void loadLedger(0);
    return () => { cancelledRef.current = true; };
  }, [loadLedger]);

  const handlePurchase = async (pack: CreditPack) => {
    setPurchasingId(pack.id);
    setPurchaseMsg(null);
    setPurchaseErr(null);
    try {
      const res = await purchaseCredits(pack.id);
      setCredits((prev) =>
        prev
          ? {
              ...prev,
              monthly_balance: res.monthly_balance,
              surplus_balance: res.surplus_balance,
              total: res.total,
            }
          : prev,
      );
      setPurchaseMsg(`Added ${pack.credits} credits to your surplus balance.`);
      await Promise.all([refreshCredits(), loadLedger(0)]);
    } catch (error_: unknown) {
      setPurchaseErr(getErrorMessage(error_, 'Purchase failed. Please try again.'));
    } finally {
      setPurchasingId(null);
    }
  };

  if (loading || !credits) return <Loading label="Loading wallet…" />;

  const resetDate = credits.next_reset_at
    ? new Date(credits.next_reset_at).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '—';

  const displayCosts = costs.length > 0 ? costs : credits.costs;

  // API responses are untrusted: read numeric fields through Partial so a
  // legacy/partial payload (a field absent from the JSON) doesn't white-screen
  // the page via `undefined.toLocaleString()`. Missing fields render as 0.
  const c = credits as Partial<Credits>;
  const total = c.total ?? 0;
  const monthlyBalance = c.monthly_balance ?? 0;
  const surplusBalance = c.surplus_balance ?? 0;
  const monthlyGrant = c.monthly_grant ?? 0;

  const lowBalance = total < 30;

  return (
    <div className="stack-6">
      <div className="stack-2">
        <span className="t-label">Credit wallet</span>
        <h1 className="t-h2" style={{ margin: 0 }}>
          Top up &amp; keep <em className="t-italic t-teal">going.</em>
        </h1>
        <p className="t-small t-mute" style={{ margin: 0 }}>
          Credits power every scan. Buy a pack once — they never expire and stack on top of your monthly plan.
        </p>
      </div>

      {/* Balance hero */}
      <section
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--s-4)',
          padding: 'var(--s-5)',
          borderRadius: 'var(--r-lg, 18px)',
          background: 'linear-gradient(120deg, var(--c-teal-weak), var(--c-cream-weak))',
          border: '1px solid var(--rule)',
        }}
        data-testid="wallet-hero"
      >
        <div
          style={{
            width: 46,
            height: 46,
            borderRadius: 14,
            background: 'var(--c-teal-weak)',
            color: 'var(--c-deep-teal)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Coin size={24} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="t-label">Your balance</div>
          <div className="t-display" style={{ fontSize: 26, lineHeight: 1, marginTop: 4 }}>
            {total.toLocaleString()}{' '}
            <span className="t-mute" style={{ fontSize: 14 }}>credits</span>
          </div>
        </div>
        {lowBalance && (
          <span className="t-small" style={{ color: 'var(--c-error)', fontWeight: 500 }}>Running low</span>
        )}
      </section>

      {/* Balance breakdown */}
      <section className="card card--bordered" data-testid="wallet-balances">
        <div className="g-3">
          <div className="stack-1">
            <span className="t-label">Monthly credits</span>
            <span className="t-h2" style={{ margin: 0 }} data-testid="monthly-balance">
              {monthlyBalance}
            </span>
            <span className="t-small t-mute">of {monthlyGrant} granted · resets {resetDate}</span>
          </div>
          <div className="stack-1">
            <span className="t-label">Surplus credits</span>
            <span className="t-h2" style={{ margin: 0 }} data-testid="surplus-balance">
              {surplusBalance}
            </span>
            <span className="t-small t-mute">bought credits — never expire</span>
          </div>
          <div className="stack-1">
            <span className="t-label">Total available</span>
            <span className="t-h2 t-teal" style={{ margin: 0 }} data-testid="total-balance">
              {total}
            </span>
            <span className="t-small t-mute">Plan: {credits.plan_name}</span>
          </div>
        </div>
      </section>

      {/* Feature cost list */}
      <section className="card card--bordered stack-4">
        <h2 className="t-h3" style={{ margin: 0 }}>What credits buy</h2>
        <ul className="stack-2" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {displayCosts.map((c) => (
            <li
              key={c.feature_key}
              style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--s-4)', alignItems: 'center' }}
            >
              <span className="t-small">{FEATURE_LABELS[c.feature_key] || c.feature_key}</span>
              <span
                className="t-small"
                style={{ fontWeight: 600, color: 'var(--c-deep-teal)', display: 'inline-flex', alignItems: 'center', gap: 5 }}
              >
                <Coin size={13} />
                {c.credit_cost}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {/* Packs */}
      <section className="stack-4">
        <h2 className="t-h3" style={{ margin: 0 }}>Buy credits</h2>
        {purchaseMsg && (
          <p className="t-small" style={{ color: 'var(--c-kivo-teal)', margin: 0 }}>{purchaseMsg}</p>
        )}
        {purchaseErr && (
          <p className="t-small" style={{ color: 'var(--c-error)', margin: 0 }}>{purchaseErr}</p>
        )}
        <div className="g-3">
          {packs.filter((p) => p.active).map((pack) => (
            <article
              key={pack.id}
              className="card card--bordered"
              style={{ display: 'flex', flexDirection: 'column' }}
              data-testid={`pack-${pack.id}`}
            >
              <header className="stack-1" style={{ marginBottom: 'var(--s-3)' }}>
                <h3 className="t-h3" style={{ margin: 0 }}>{pack.name}</h3>
                <span className="t-small t-mute">{pack.credits} credits</span>
              </header>
              <div className="plan-price" style={{ marginBottom: 'var(--s-4)' }}>
                <span className="plan-price__amount">{formatPrice(pack.price_cents, pack.currency)}</span>
              </div>
              <button
                type="button"
                onClick={() => handlePurchase(pack)}
                disabled={purchasingId === pack.id}
                className="btn btn--primary btn--full"
                style={{ marginTop: 'auto' }}
              >
                {purchasingId === pack.id ? 'Processing…' : `Buy ${pack.credits} credits`}
              </button>
            </article>
          ))}
          {packs.filter((p) => p.active).length === 0 && (
            <p className="t-small t-mute" style={{ margin: 0 }}>No packs available right now.</p>
          )}
        </div>
      </section>

      {/* Ledger / history */}
      <section className="card card--bordered stack-4">
        <h2 className="t-h3" style={{ margin: 0 }}>History</h2>
        {ledger.length === 0 ? (
          <p className="t-small t-mute" style={{ margin: 0 }}>No credit activity yet.</p>
        ) : (
          <ul className="stack-2" style={{ listStyle: 'none', padding: 0, margin: 0 }} data-testid="ledger-list">
            {ledger.map((e) => (
              <li
                key={e.id}
                style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--s-4)', alignItems: 'baseline' }}
              >
                <span className="t-small">
                  {EVENT_LABELS[e.event_type] || e.event_type}
                  {e.feature_key ? ` · ${FEATURE_LABELS[e.feature_key] || e.feature_key}` : ''}
                  <span className="t-mute" style={{ marginLeft: 'var(--s-2)' }}>
                    {new Date(e.created_at).toLocaleString()}
                  </span>
                </span>
                <span
                  className="t-small"
                  style={{ fontWeight: 600, color: e.delta >= 0 ? 'var(--c-kivo-teal)' : 'var(--c-error)' }}
                >
                  {e.delta >= 0 ? '+' : ''}{e.delta} ({e.balance_type})
                </span>
              </li>
            ))}
          </ul>
        )}
        {ledgerTotal > LEDGER_PAGE_SIZE && (
          <div className="row-3" style={{ justifyContent: 'space-between' }}>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={ledgerOffset === 0 || ledgerLoading}
              onClick={() => loadLedger(Math.max(0, ledgerOffset - LEDGER_PAGE_SIZE))}
            >
              ← Newer
            </button>
            <span className="t-small t-mute">
              {ledgerOffset + 1}–{Math.min(ledgerOffset + LEDGER_PAGE_SIZE, ledgerTotal)} of {ledgerTotal}
            </span>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={ledgerOffset + LEDGER_PAGE_SIZE >= ledgerTotal || ledgerLoading}
              onClick={() => loadLedger(ledgerOffset + LEDGER_PAGE_SIZE)}
            >
              Older →
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
